import { routes } from "../apiroutes";
import HttpService from "../httpServices";
import useFetchItem from "../useFetchItem";
import useMutateItem from "../useMutateItem";
import { ErrorHandler } from "../errorHandler";
import { showErrorAlert, showSuccessAlert } from "../../utils/alert";

const httpService = new HttpService();

// Performance Reports Hooks
export const useGetEmployeePerformance = ({ employeeId, enabled = true, filters = {} } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["GetEmployeePerformance", employeeId, filters],
    queryFn: () => httpService.getData(routes.getEmployeePerformance(employeeId, filters)),
    enabled: enabled && !!employeeId,
    retry: 2
  });

  return {
    employeePerformanceIsFetching: isFetching,
    employeePerformanceIsFetched: isFetched,
    employeePerformanceIsLoading: isLoading,
    employeePerformanceError: ErrorHandler(error),
    employeePerformanceData: data?.data || {},
    refetchEmployeePerformance: refetch
  };
};

export const useGetPerformanceRanking = ({ enabled = true, filters = {} } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["GetPerformanceRanking", filters],
    queryFn: () => httpService.getData(routes.getPerformanceRanking(filters)),
    enabled,
    retry: 2
  });

  return {
    performanceRankingIsFetching: isFetching,
    performanceRankingIsFetched: isFetched,
    performanceRankingIsLoading: isLoading,
    performanceRankingError: ErrorHandler(error),
    performanceRankingData: data?.data || {},
    refetchPerformanceRanking: refetch
  };
};

export const useGetPerformanceInsights = ({ enabled = true } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["GetPerformanceInsights"],
    queryFn: () => httpService.getData(routes.getPerformanceInsights()),
    enabled,
    retry: 2
  });

  return {
    performanceInsightsIsFetching: isFetching,
    performanceInsightsIsFetched: isFetched,
    performanceInsightsIsLoading: isLoading,
    performanceInsightsError: ErrorHandler(error),
    performanceInsightsData: data?.data || {},
    refetchPerformanceInsights: refetch
  };
};

export const useCreatePerformanceGoal = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.postData(payload, routes.createPerformanceGoal(payload)),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Performance goal created successfully");
      handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    createGoalData: data,
    createGoalError: ErrorHandler(error),
    createGoalIsLoading: isPending,
    createGoalPayload: (requestPayload) => mutate(requestPayload),
  };
};

export const useGetEmployeeGoals = ({ employeeId, enabled = true, filters = {} } = {}) => {
  const { isFetched, isLoading, error, data, refetch, isFetching } = useFetchItem({
    queryKey: ["GetEmployeeGoals", employeeId, filters],
    queryFn: () => httpService.getData(routes.getEmployeeGoals(employeeId, filters)),
    enabled: enabled && !!employeeId,
    retry: 2
  });

  return {
    employeeGoalsIsFetching: isFetching,
    employeeGoalsIsFetched: isFetched,
    employeeGoalsIsLoading: isLoading,
    employeeGoalsError: ErrorHandler(error),
    employeeGoalsData: data?.data || [],
    refetchEmployeeGoals: refetch
  };
};

export const useUpdatePerformanceGoal = (handleSuccess) => {
  const { data, error, isPending, mutate } = useMutateItem({
    mutationFn: (payload) =>
      httpService.putData(payload, routes.updatePerformanceGoal(payload.goal_id)),
    onSuccess: (res) => {
      const resData = res?.data || {};
      showSuccessAlert(resData?.message || "Performance goal updated successfully");
      handleSuccess(resData);
    },
    onError: (err) => {
      const error = ErrorHandler(err);
      showErrorAlert(error);
    },
  });

  return {
    updateGoalData: data,
    updateGoalError: ErrorHandler(error),
    updateGoalIsLoading: isPending,
    updateGoalPayload: (requestPayload) => mutate(requestPayload),
  };
}; 